import React, { useState, useEffect, useRef } from 'react';

/**
 * Multi-select Project Manager pill list. Click the row to open a
 * dropdown of available PMs (from pmList). Each selected PM renders as
 * an accent-soft pill with initials swatch + name + × remove button.
 *
 * Props:
 *   value     — current PM names (comma-separated string, matches the
 *               project_manager schema)
 *   pmList    — [{ id, name, role_label }, ...] available PMs
 *   pending   — boolean; render a small accent dot on the right
 *   onChange(commaSeparatedNames) — commit handler
 */
export default function PMPills({ value, pmList = [], pending = false, onChange, single = false, addLabel = '+ Add manager' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = (value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const remove = (name) => {
    const next = selected.filter((n) => n !== name);
    onChange && onChange(next.join(', '));
  };
  const add = (name) => {
    if (single) { onChange && onChange(name); setOpen(false); return; }
    if (selected.includes(name)) return;
    onChange && onChange([...selected, name].join(', '));
    setOpen(false);
  };

  const available = pmList.filter((pm) => !selected.includes(pm.name));

  const initialsOf = (full) => {
    const parts = (full || '').split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
        padding: 6,
        borderRadius: 8,
        background: open ? 'var(--bg-surface)' : 'transparent',
        border: `1px solid ${open ? 'var(--accent)' : 'transparent'}`,
        boxShadow: open ? '0 0 0 3px var(--accent-ring)' : 'none',
        cursor: 'pointer',
        transition: 'background .12s, border-color .12s',
        minHeight: 36,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(true); }}
    >
      {selected.map((name) => (
        <span
          key={name}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 8px 3px 3px',
            background: 'var(--accent-soft)',
            border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
            borderRadius: 999,
            fontSize: 13.5,
            fontWeight: 500,
            color: 'var(--accent-text)',
          }}
        >
          <span
            className="mono"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 18, height: 18, borderRadius: 999,
              background: 'color-mix(in oklch, var(--accent) 25%, transparent)',
              fontSize: 11, fontWeight: 600,
            }}
          >
            {initialsOf(name)}
          </span>
          {name}
          <span
            onClick={(e) => { e.stopPropagation(); remove(name); }}
            style={{ cursor: 'pointer', color: 'color-mix(in oklch, var(--accent-text) 70%, transparent)', fontSize: 15.5, lineHeight: 1 }}
            aria-label={`Remove ${name}`}
          >×</span>
        </span>
      ))}
      {(!single || selected.length === 0) && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 10px',
            border: '1px dashed var(--border-default)',
            borderRadius: 999,
            background: 'transparent',
            color: 'var(--text-3)',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {addLabel}
        </button>
      )}

      {pending && (
        <span
          aria-hidden
          style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            width: 6, height: 6, borderRadius: 999, background: 'var(--accent)',
          }}
        />
      )}

      {open && available.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-md)',
            padding: 4,
            zIndex: 50,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {available.map((pm) => (
            <div
              key={pm.id || pm.name}
              onClick={(e) => { e.stopPropagation(); add(pm.name); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14.5,
                color: 'var(--text-1)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span
                className="mono"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: 6,
                  background: 'var(--bg-surface-2)',
                  border: '1px solid var(--border-subtle)',
                  fontSize: 11, fontWeight: 600,
                  color: 'var(--text-2)',
                }}
              >
                {initialsOf(pm.name)}
              </span>
              <span style={{ flex: 1 }}>{pm.name}</span>
              {pm.role_label && (
                <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{pm.role_label}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
